import React, { useEffect, useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { encodeFiltersToURL, decodeURLToFilters } from 'utils/urlParams';
import { useQueryClient } from '@tanstack/react-query';
import keys from 'constants/queryKeys';
import Select from 'react-select';
import {
  OverlayTrigger,
  Tab,
  Dropdown,
  Nav,
  DropdownButton,
} from 'react-bootstrap';
import { BiDisc } from 'react-icons/bi';
import { BsFilter, BsPlus } from 'react-icons/bs';
import { FiTag, FiUser, FiFile, FiFileText, FiBriefcase } from 'react-icons/fi';
import { RxCross2 } from 'react-icons/rx';
import { TbCalendarMinus, TbBuildingCommunity } from 'react-icons/tb';

import { setLoader } from 'redux/slices/loader';
import BSButton from 'Components/Button/BSButton';
import Calender from 'Components/Calender/Calender';
import SearchInput from 'ui/SearchInput/SearchInput';
import KanbanBoard from 'Containers/KanbanBoard/KanbanBoard';
import KanbanList from 'Containers/KanbanList/KanbanList';
import MoveToStateModal from 'Containers/Modal/MoveToStateModal/MoveToStateModal';
import BriefDrawer from 'Components/Drawers/BriefDrawer/BriefDrawer';
import { formattedDate } from 'helpers';
import ConfirmDeleteModal from 'Containers/Modal/ConfirmDeleteModal/ConfirmDeleteModal';
import {
  error as renderError,
  success as renderSuccess,
} from 'helpers/toaster.js';
import {
  deleteTask,
  updateTask,
  getDashboardConfig,
  updateDashboardConfig,
  addTask,
} from 'services/contentPlannerService';
import onBoardingService from 'services/onBoardingService';
import ContentFilter from './ContentFilter';
import FilterChipDropdown from 'Components/FilterChipDropdown/FilterChipDropdown';
import Styles from './ContentPlanner.module.scss';
import ProjectSelect from './ProjectSelect';
import { ROUTES } from 'constants/routes';
import { StatusOptions } from 'Components/Status/StatusOption';
import { CONTENT_TYPE_OPTIONS } from 'constants/contentTypes';
import FloatingButton from 'ui/FloatingButton/FloatingButton';
import Popover from 'Components/Popover/Popover';
import ImageView from 'Components/Image/ImageView';
import EmptyState from 'Components/EmptyState/EmptyState';
import { archiveTask } from 'services/taskService';
import ViewToggle from 'Components/ViewToggle/ViewToggle';
import useBriefs from 'hooks/briefs/useBriefs';
import Loader from 'Components/Loader/Loader';
import useCreateBrief from 'hooks/briefs/useCreateBrief';
import useDashboardConfig from 'hooks/briefs/useDashboardConfig';
import { ReactComponent as List } from 'assets/svg/list.svg';
import { ReactComponent as Calendar } from 'assets/svg/calendar.svg';
import { ReactComponent as Kanban } from 'assets/svg/kanban.svg';
import { IoIosList } from 'react-icons/io';
import { TableSkeleton } from 'Components/TableSkeleton/TableSkeleton';
import { FaChevronLeft, FaChevronRight, FaChevronDown } from 'react-icons/fa6';
import { AiOutlineProject } from 'react-icons/ai';
import { LuPlus } from 'react-icons/lu';
import MultiDropdown from 'Components/MultiDropdown/MultiDropdown';
import Drawer from 'Components/Drawer/Drawer';
import ExpandPlanDrawer from 'Components/Drawers/ExpandPlanDrawer/ExpandPlanDrawer';
import { generateStreamResponse } from 'helpers/openAIChat';
import { error as errorToast } from 'helpers/toaster';
import useCreateDocument from 'hooks/documents/useCreateDocument';
import ContentGenerationModal from 'Containers/Modal/ContentGenerationModal/ContentGenerationModal';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import Announcement from 'Components/Pages/Brand/Announcement';
import BulkActionToolbar from 'Components/BulkActionToolbar/BulkActionToolbar';
import BulkDeleteConfirmModal from 'Containers/Modal/BulkDeleteConfirmModal/BulkDeleteConfirmModal';
import useBulkDeleteTasks from 'hooks/briefs/useBulkDeleteTasks';
import { DEFAULT_VIEW_MODE, VIEW_MODE_STORAGE_KEY } from '../../config/groupingConfig';
import useProjects from 'hooks/projects/useProjects';
import useProject from 'hooks/projects/useProject';

const optionsClassName =
  'd-flex align-items-center select-option ' + Styles.SelectLabel;

const options = [
  {
    value: 'state',
    label: (
      <div className={optionsClassName}>
        <BiDisc size={14} color="#667085" /> <p>Status</p>
      </div>
    ),
  },
  {
    value: 'deadline_date',
    label: (
      <div className={optionsClassName}>
        <TbCalendarMinus size={14} color="#667085" /> <p>Deadline</p>
      </div>
    ),
  },
  {
    value: 'assignee_id',
    label: (
      <div className={optionsClassName}>
        <FiUser size={14} color="#667085" /> <p>Assigned to</p>
      </div>
    ),
  },
  {
    value: 'content_type',
    label: (
      <div
        className={
          'd-flex align-items-center select-option ' + Styles.SelectLabel
        }
      >
        <FiTag size={14} color="#667085" /> <p>Content Type</p>
      </div>
    ),
  },
  {
    value: 'brand_id',
    label: (
      <div className={optionsClassName}>
        <TbBuildingCommunity size={14} color="#667085" /> <p>Brand</p>
      </div>
    ),
  },
  {
    value: 'project_id',
    label: (
      <div className={optionsClassName}>
        <FiBriefcase size={14} color="#667085" /> <p>Campaign</p>
      </div>
    ),
  },
];

const isFilterOptions = [
  {
    value: '_in',
    label: <div className={optionsClassName}>is</div>,
  },
  {
    value: '_not_in',
    label: <div className={optionsClassName}>is not</div>,
  },
];

const SelectStyles = {
  option: (styles, { isSelected, isFocused }) => {
    return {
      boxShadow: isFocused ? null : null,
      background: isSelected ? `url("./Images/blueTick.svg")` : '',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '90% 50%',
      backgroundColor: isSelected ? '#F2FBFB!important' : '#fff',
      paddingRight: isSelected ? '0px' : '25px',
      fontSize: '14px',
      lineHeight: '24px',
      fontWeight: '500',
      color: '#344054',

      '&:hover': {
        backgroundColor: 'lightgrey',
        cursor: 'pointer',
      },
    };
  },

  control: (baseStyles) => {
    return {
      ...baseStyles,
      cursor: 'pointer',
      minHeight: '32px',
      fontSize: '13px',
    };
  },

  valueContainer: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '2px 8px',
    };
  },

  indicatorsContainer: (baseStyles) => {
    return {
      ...baseStyles,
      height: '32px',
    };
  },

  dropdownIndicator: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '4px',
    };
  },
};

const filterOptions = {
  filterBy: '',
  isOptions: '_in', // Default to "is" to reduce clicks
};

const parseFilters = (filters) => {
  return filters.reduce((acc, filter) => {
    const { filterBy, isOptions, filterValue } = filter || {};
    let val = '';
    if (['state', 'assignee_id', 'content_type', 'brand_id', 'project_id'].includes(filterBy)) {
      val = filterValue
        ? filterValue.reduce((accFilter, filterVal, index) => {
            if (index + 1 === filterValue.length) {
              accFilter += filterVal.value;
              return accFilter;
            }
            accFilter += `${filterVal.value},`;
            return accFilter;
          }, '')
        : '';
    } else {
      val = filterValue ? moment(filterValue).format('DD-MM-YYYY') : '';
    }

    // Map brand_id to project_business_id for backend ransack query
    const searchKey = filterBy === 'brand_id' ? 'project_business_id' : filterBy;
    acc += filterBy ? `search[${searchKey}${isOptions}]=${val}&` : '';
    return acc;
  }, '');
};

const ContentPlanner = ({ projectView = false, noMargin, campaignBrandId }) => {
  const [showBriefDrawer, setShowBriefDrawer] = useState(false);
  const [editTaskModalView, setEditTaskModalView] = useState(false);
  const [deleteTaskModalView, setDeleteTaskModalView] = useState(false);
  const [moveToStateModalView, setMoveToStateModalView] = useState(false);
  const [newTask, setnewTask] = useState({});
  const [taskIdToDelete, setTaskIdToDelete] = useState(null);
  const [taskIdToEdit, setTaskIdToEdit] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Initialize state from URL params if present, otherwise use defaults
  const initialStateFromURL = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.toString()) {
      return decodeURLToFilters(params);
    }
    return null;
  }, []); // Empty dependency - only run once on mount

  const [filters, setFilters] = useState(
    initialStateFromURL?.filtersArray || [filterOptions]
  );
  const [finalFilters, setFinalFilters] = useState(
    initialStateFromURL?.filtersArray || [filterOptions]
  );
  const [searchValue, setSearchValue] = useState(
    initialStateFromURL?.searchValue || ''
  );
  const [showPopover, setShowPopover] = useState(false);
  const [tasks, setTasks] = useState([]);
  const ACTIVE_TAB_STORAGE_KEY = 'contentPlanner_activeTab';
  const CALENDAR_VIEW_STORAGE_KEY = 'contentPlanner_calendarView';
  const [activeTab, setActiveTab] = useState(
    localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || 'list'
  );
  const [editingFilterIndex, setEditingFilterIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const chipDropdownRef = useRef(null);
  const [listViewMode, setListViewMode] = useState(
    initialStateFromURL?.viewMode ||
    localStorage.getItem(VIEW_MODE_STORAGE_KEY) ||
    DEFAULT_VIEW_MODE
  );
  const [date, setDate] = useState(new Date());
  const calendarRef = useRef(null);
  const [calendarView, setCalendarView] = useState(
    localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY) || 'month'
  ); // 'week' or 'month'
  const [modalView, setModalView] = useState({
    expandPlan: false,
    createBrief: false,
    // addProject: false,
  });
  const [taskInfo, setTaskInfo] = useState({
    taskId: null,
    taskName: null,
    projectId: null,
  });
  const [showContentGeneration, setShowContentGeneration] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [contentGenerated, setContentGenerated] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const abortController = useRef();
  // Refs to track latest values for closure safety in handleContentGenerated
  const streamContentRef = useRef('');
  const taskInfoRef = useRef({ taskId: null, taskName: null, projectId: null });
  const { user } = useSelector((state) => state.auth);
  const { id: userId } = user || {};

  // Flag to prevent URL sync during initial mount
  const isInitialMount = useRef(true);
  const isUpdatingFromURL = useRef(false);

  const { subscriptions, user_brands } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const { mutateAsync: createDocument } = useCreateDocument({
    userId,
  });
  const { mutateAsync: bulkDeleteTasks, isLoading: isBulkDeleting } =
    useBulkDeleteTasks();

  // Fetch projects data for campaign view grouping
  const { data: projectsData } = useProjects({ searchTerm: '', enabled: true });

  // Extract projects array from paginated data
  const projects = useMemo(() => {
    return projectsData?.pages?.flatMap((page) => page?.data?.projects || []) || [];
  }, [projectsData?.pages]);

  // Fetch brands data for brand view grouping
  const [brands, setBrands] = useState(() => {
    // Initialize from localStorage cache if available
    try {
      const cached = localStorage.getItem('vocable_brands_cache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await onBoardingService.getUserBrandInfo();
        const brandsData = response?.data?.brands_data || [];
        setBrands(brandsData);

        // Cache brands in localStorage for instant access on navigation
        try {
          localStorage.setItem('vocable_brands_cache', JSON.stringify(brandsData));
        } catch (error) {
          console.warn('Failed to cache brands:', error);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchBrands();
  }, []);

  // Fetch dashboard column configuration with caching
  const { data: selectedColumns, refetch: refetchColumns } = useDashboardConfig();

  const dropdownItems = [
    {
      modalKey: 'expandPlan',
      Icon: FiFile,
      label: 'Expand Plan',
    },
    {
      modalKey: 'createBrief',
      Icon: FiFileText,
      label: 'Create Brief',
    },
    // {
    //   modalKey: 'addProject',
    //   Icon: AiOutlineProject,
    //   label: 'Campaign',
    // },
  ];

  const { briefId, projectId } = useParams();

  // Fetch project data to get start_date and end_date for deadline calculations
  const { project: campaignData } = useProject({ projectId });

  const {
    data: briefsPages,
    refetch,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useBriefs({
    searchValue,
    filterStr: parseFilters(finalFilters),
    selectedProject: projectId || selectedProject,
  });

  const { mutateAsync: createBrief } = useCreateBrief({ userId: user?.id });

  // Use useLayoutEffect to update tasks synchronously before paint
  // This prevents the flash of empty state while useEffect is pending
  useLayoutEffect(() => {
    const currentTasks =
      briefsPages?.pages
        ?.flatMap((page) => page?.data?.tasks || [])
        ?.filter((task) => !!task) || [];

    setTasks(currentTasks);
  }, [briefsPages?.pages]);

  // Keep refs in sync with state to avoid stale closures in handleContentGenerated
  useEffect(() => {
    streamContentRef.current = streamContent;
  }, [streamContent]);

  useEffect(() => {
    taskInfoRef.current = taskInfo;
  }, [taskInfo]);

  useEffect(() => {
    if (contentGenerated) {
      handleContentGenerated();
    }
  }, [contentGenerated]);

  useEffect(() => {
    if (tasks?.some((task) => Number(task.id) === Number(briefId))) {
      onEditHandler(Number(briefId));
    }
  }, [briefId, tasks]);

  // Adjust date to proper starting point when calendar view changes
  useEffect(() => {
    const today = new Date();

    if (calendarView === 'week') {
      // For week view, set to Monday of current week
      const currentDay = today.getDay();
      const diff = today.getDate() - currentDay + (currentDay === 0 ? -6 : 1); // Adjust for Sunday
      const monday = new Date(today);
      monday.setDate(diff);
      setDate(monday);
    } else {
      // For month view, set to first day of current month
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      setDate(firstOfMonth);
    }
  }, [calendarView]);

  // Sync URL params when filters or view mode changes (push to history)
  useEffect(() => {
    // Skip URL update on initial mount or when updating from URL
    if (isInitialMount.current) {
      return;
    }

    if (isUpdatingFromURL.current) {
      return;
    }

    // Encode current state to URL params
    const params = encodeFiltersToURL(finalFilters, listViewMode, searchValue);

    // Push to history (creates new history entry for back button)
    setSearchParams(params, { replace: false });
  }, [finalFilters, listViewMode, setSearchParams]);

  // Separate useEffect for search with debounce (replace history)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isUpdatingFromURL.current) {
      return;
    }

    // Debounce search updates by 300ms
    const timer = setTimeout(() => {
      const params = encodeFiltersToURL(finalFilters, listViewMode, searchValue);
      // Replace current history entry (no new entry for search)
      setSearchParams(params, { replace: true });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, finalFilters, listViewMode, setSearchParams]);

  const onDeleteHandler = useCallback((id) => {
    setDeleteTaskModalView(true);
    setTaskIdToDelete(id);
  }, []);

  const handleViewModeChange = useCallback((newViewMode) => {
    setListViewMode(newViewMode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, newViewMode);
  }, []);

  const handleNextMonth = () => {
    // Update date state for display based on view mode
    let nextDate;
    if (calendarView === 'week') {
      // Week view: add 7 days (1 week)
      nextDate = new Date(date);
      nextDate.setDate(date.getDate() + 7);
    } else {
      // Month view: add 1 month
      nextDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }
    setDate(nextDate);

    // Scroll to next period in the scrollable calendar
    if (calendarRef.current) {
      const scrollContainer = calendarRef.current.querySelector('.vocable-calender-scrollable');
      if (scrollContainer) {
        const monthContainers = scrollContainer.querySelectorAll('.calendar-month-container');

        // Calculate the appropriate index based on view mode
        let targetIndex;
        const today = new Date();

        if (calendarView === 'week') {
          // Week view: Calculate Monday of current week as reference
          const currentDay = today.getDay();
          const mondayOfCurrentWeek = new Date(today);
          mondayOfCurrentWeek.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1));

          // Calculate weeks difference from current week's Monday
          const weeksDiff = Math.round((nextDate - mondayOfCurrentWeek) / (1000 * 60 * 60 * 24 * 7));
          targetIndex = Math.max(0, Math.min(monthContainers.length - 1, 4 + weeksDiff));
        } else {
          // Month view: Use first of current month as reference
          const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const monthsDiff = (nextDate.getFullYear() - firstOfCurrentMonth.getFullYear()) * 12 +
                            (nextDate.getMonth() - firstOfCurrentMonth.getMonth());
          targetIndex = Math.max(0, Math.min(monthContainers.length - 1, 3 + monthsDiff));
        }

        if (monthContainers[targetIndex]) {
          monthContainers[targetIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }
  };

  const handlePreviousMonth = () => {
    // Update date state for display based on view mode
    let prevDate;
    if (calendarView === 'week') {
      // Week view: subtract 7 days (1 week)
      prevDate = new Date(date);
      prevDate.setDate(date.getDate() - 7);
    } else {
      // Month view: subtract 1 month
      prevDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }
    setDate(prevDate);

    // Scroll to previous period in the scrollable calendar
    if (calendarRef.current) {
      const scrollContainer = calendarRef.current.querySelector('.vocable-calender-scrollable');
      if (scrollContainer) {
        const monthContainers = scrollContainer.querySelectorAll('.calendar-month-container');

        // Calculate the appropriate index based on view mode
        let targetIndex;
        const today = new Date();

        if (calendarView === 'week') {
          // Week view: Calculate Monday of current week as reference
          const currentDay = today.getDay();
          const mondayOfCurrentWeek = new Date(today);
          mondayOfCurrentWeek.setDate(today.getDate() - currentDay + (currentDay === 0 ? -6 : 1));

          // Calculate weeks difference from current week's Monday
          const weeksDiff = Math.round((prevDate - mondayOfCurrentWeek) / (1000 * 60 * 60 * 24 * 7));
          targetIndex = Math.max(0, Math.min(monthContainers.length - 1, 4 + weeksDiff));
        } else {
          // Month view: Use first of current month as reference
          const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const monthsDiff = (prevDate.getFullYear() - firstOfCurrentMonth.getFullYear()) * 12 +
                            (prevDate.getMonth() - firstOfCurrentMonth.getMonth());
          targetIndex = Math.max(0, Math.min(monthContainers.length - 1, 3 + monthsDiff));
        }

        if (monthContainers[targetIndex]) {
          monthContainers[targetIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }
  };

  // Format display label based on view mode
  const formattedMonthYear = useMemo(() => {
    if (calendarView === 'week') {
      // For week view, show week range (e.g., "Jan 15-21, 2025")
      const weekStart = new Date(date);
      const weekEnd = new Date(date);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startMonth = weekStart.toLocaleString('default', { month: 'short' });
      const endMonth = weekEnd.toLocaleString('default', { month: 'short' });
      const year = weekStart.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${weekStart.getDate()}-${weekEnd.getDate()}, ${year}`;
      } else {
        return `${startMonth} ${weekStart.getDate()} - ${endMonth} ${weekEnd.getDate()}, ${year}`;
      }
    } else {
      // For month view, show month and year (e.g., "January 2025")
      return date.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      });
    }
  }, [date, calendarView]);

  const updateColumns = useCallback(async (columns) => {
    try {
      await updateDashboardConfig(columns);
      refetchColumns(); // Refetch to update cache
      renderSuccess('Updated column configuration');
    } catch (e) {
      renderError('Cannot save column configuration');
    }
  }, [refetchColumns]);

  const onConfirmDeleteHandler = useCallback(async () => {
    try {
      const response = await deleteTask(taskIdToDelete);
      tasks?.filter((task) => {
        return task.id !== response?.data?.task.id;
      });

      if (editTaskModalView) {
        setEditTaskModalView(false);
      }

      refetch();
      renderSuccess(response?.data?.message);
    } catch (error) {
      renderError(error);
    }
  }, [taskIdToDelete, tasks, editTaskModalView, refetch]);

  const updateTaskInTasksArray = useCallback((taskId, updatedTask) => {
    // Update local state immediately for instant UI update (optimistic)
    // Handle both string and number IDs for compatibility with FullCalendar
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        String(task.id) === String(taskId) ? { ...task, ...updatedTask } : task
      )
    );
  }, []);

  const onUpdateBrief = useCallback((id, brief) => {
    updateTaskInTasksArray(id, brief);
  }, [updateTaskInTasksArray]);

  const onMoveHandler = useCallback((id) => {
    setMoveToStateModalView(true);
    setTaskIdToEdit(id);
  }, []);

  const onEditHandler = useCallback((id) => {
    setEditTaskModalView(true);
    setTaskIdToEdit(id);
  }, []);

  const onDuplicateHandler = useCallback(async (id) => {
    const taskToDuplicate = tasks.find((task) => task.id === id);
    if (!taskToDuplicate) {
      renderError('Task not found');
      return;
    }

    // Create optimistic duplicate with temporary ID
    const tempId = `temp-${Date.now()}`;
    const { id: _id, created_at, updated_at, ...taskData } = taskToDuplicate;
    const optimisticDuplicate = {
      ...taskData,
      id: tempId,
      title: `${taskData.title} (Copy)`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Add to UI immediately
    setTasks((prevTasks) => [...prevTasks, optimisticDuplicate]);

    try {
      // Make API call in background
      const duplicatePayload = {
        task: {
          ...taskData,
          title: `${taskData.title} (Copy)`,
        },
        project_id: taskData.project_id,
      };

      const response = await addTask(duplicatePayload);

      // Replace temporary task with real task from server
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === tempId ? response?.data?.task : task
        )
      );

      renderSuccess('Brief duplicated successfully!');
    } catch (error) {
      // Remove optimistic duplicate on error
      setTasks((prevTasks) =>
        prevTasks.filter((task) => task.id !== tempId)
      );
      renderError(error?.response?.data?.message || 'Failed to duplicate brief');
    }
  }, [tasks]);

  const onArchive = useCallback(async (id) => {
    dispatch(
      setLoader({
        isLoading: true,
        heading: 'Archiving Brief!',
        description: 'Please wait...',
      }),
    );
    try {
      const response = await archiveTask(id);
      refetch();
      renderSuccess(response?.data?.message);
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    } catch (error) {
      refetch();
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
      renderError(error);
    }
  }, [dispatch, refetch]);

  const onConfirmEditHandler = useCallback(async (payload) => {
    try {
      const { project_id: projectId, ...remainingPayload } = payload;
      const response = await updateTask(taskIdToEdit, {
        task: {
          deadline_date: payload.deadline_date
            ? formattedDate(payload.deadline_date)
            : null,
          ...remainingPayload,
        },
        project_id: projectId,
      });

      tasks?.map((task) => {
        if (task.id === taskIdToEdit) {
          return { ...task, ...response?.data?.task };
        }
        return task;
      });

      refetch();
      renderSuccess(response?.data?.message);
    } catch (error) {
      renderError(error);
    }
  }, [taskIdToEdit, tasks, refetch]);

  const onCreateHandler = useCallback((category) => {
    setnewTask({ state: category });
    // setShowBriefDrawer(true);
    setModalView({
      expandPlan: false,
      createBrief: true,
      // [item.modalKey]: true,
    });
  }, []);

  const onCreatePostWithDate = useCallback((date) => {
    setnewTask({
      state: 'not_started',
      deadline_date: date,
    });
    setModalView({
      expandPlan: false,
      createBrief: true,
    });
  }, []);

  // Quick update handler for drag-and-drop (no drawer opening)
  const onQuickUpdateDeadline = useCallback(async (taskId, newDate) => {
    // Find the task to update (handle both string and number IDs from FullCalendar)
    const taskToUpdate = tasks?.find(t => String(t.id) === String(taskId));
    if (!taskToUpdate) return;

    // Optimistic update - update UI immediately
    const optimisticTask = {
      ...taskToUpdate,
      deadline_date: newDate,
    };
    updateTaskInTasksArray(taskId, optimisticTask);

    try {
      // Make API call to update deadline
      const response = await updateTask(taskToUpdate.id, {
        task: {
          deadline_date: formattedDate(newDate),
        },
        project_id: taskToUpdate.project_id,
      });

      // Update with actual response from server and refetch to ensure sync
      updateTaskInTasksArray(taskId, response?.data?.task);
      refetch();
    } catch (error) {
      // Revert optimistic update on error
      updateTaskInTasksArray(taskId, taskToUpdate);
      renderError(error);
    }
  }, [tasks, updateTaskInTasksArray, refetch]);

  const onConfirmCreateHandler = async (payload) => {
    await createBrief({
      ...payload,
      deadline_date: payload.deadline_date
        ? formattedDate(payload.deadline_date)
        : '',
    });
  };

  const onSubmitHandler = async (action, payload) => {
    if (action === 'create') {
      await onConfirmCreateHandler(payload);
    }

    if (action === 'edit') {
      await onConfirmEditHandler(payload);
    }
  };

  const handleFilters = useCallback((index, val, type) => {
    const transformedFilters = filters.map((filter, filIndex) => {
      if (filIndex === index) {
        if (type === 'isOptions') {
          return {
            ...filter,
            [type]: val,
          };
        }

        // When filterBy is selected, auto-set isOptions to '_in' (is)
        if (type === 'filterBy') {
          return {
            ...filter,
            filterBy: val,
            isOptions: '_in', // Auto-select "is" to reduce clicks
            filterValue: '',
          };
        }

        if (type !== 'filterValue') {
          return {
            ...filter,
            [type]: val,
            filterValue: '',
          };
        }

        return {
          ...filter,
          [type]: val,
        };
      }

      return filter;
    });

    if (type === 'filterValue') {
      setFinalFilters(transformedFilters);
    }
    setFilters(transformedFilters);
  }, [filters]);

  const handleRemoveFilter = useCallback((index) => {
    if (filters.length === 1) {
      setFilters([{ filterBy: '', isOptions: '_in', filterValue: '' }]); // Default to "is"
    }

    const transformedFilters = [...filters];
    transformedFilters.splice(index, 1);

    setFilters(transformedFilters);
    setFinalFilters(transformedFilters);
    refetch();
  }, [filters, refetch]);

  const appliedFilters = filters?.map((filter) => filter?.filterBy) || [];

  // Helper function to generate filter chip labels
  const getFilterLabel = useCallback((filter) => {
    const { filterBy, isOptions, filterValue } = filter;

    // Map field names to user-friendly labels
    const fieldNameMap = {
      'state': 'Status',
      'deadline_date': 'Deadline',
      'assignee_id': 'Assigned to',
      'content_type': 'Content Type',
      'brand_id': 'Brand',
      'project_id': 'Campaign',
    };

    const fieldName = fieldNameMap[filterBy] || filterBy;
    const operator = isOptions === '_in' ? 'is' : 'is not';

    // Handle date filters
    if (filterBy === 'deadline_date') {
      return `${fieldName}: ${moment(filterValue).format('MMM DD, YYYY')}`;
    }

    // Handle multi-value filters
    if (Array.isArray(filterValue) && filterValue.length > 1) {
      return `${fieldName}: ${filterValue.length} selected`;
    }

    // Handle single value
    if (Array.isArray(filterValue) && filterValue.length === 1) {
      const filterItem = filterValue[0];
      let valueName = '';

      // For status filters - format properly (e.g., "in_progress" -> "In Progress")
      if (filterBy === 'state') {
        valueName = String(filterItem.value || filterItem.label || '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
      }
      // For content type filters
      else if (filterBy === 'content_type') {
        const contentType = CONTENT_TYPE_OPTIONS.find(c => c.value === filterItem.value);
        if (contentType) {
          valueName = contentType.label;
        } else {
          valueName = filterItem.label || filterItem.value;
        }
      }
      // For brand filters - look up brand name from ID
      else if (filterBy === 'brand_id') {
        if (filterItem.label && typeof filterItem.label === 'string') {
          valueName = filterItem.label;
        } else {
          // Fallback: look up from user_brands
          const brandsData = user?.user_brands?.brands_data || [];
          const brandObj = brandsData.find(b => Object.keys(b)[0] === String(filterItem.value));
          if (brandObj) {
            const brandData = Object.values(brandObj)[0];
            valueName = brandData?.brand_name || brandData || 'Unknown';
          } else {
            valueName = String(filterItem.value);
          }
        }
      }
      // For assignee and project filters - use label if available
      else if (filterBy === 'assignee_id' || filterBy === 'project_id') {
        valueName = filterItem.label || String(filterItem.value);
      }
      // For other filters - use value directly
      else if (typeof filterItem.value === 'string') {
        valueName = filterItem.value;
      }

      // Fallback: Try to extract from nested React elements
      if (!valueName && filterItem.label?.props?.children) {
        const children = filterItem.label.props.children;

        if (Array.isArray(children)) {
          for (const child of children) {
            if (child?.type === 'label' && child?.props?.children) {
              valueName = child.props.children;
              break;
            }
            if (child?.type === 'div' && child?.props?.children) {
              const nestedLabel = child.props.children;
              if (nestedLabel?.type === 'label' && nestedLabel?.props?.children) {
                valueName = nestedLabel.props.children;
                break;
              }
            }
          }
        } else if (typeof children === 'string') {
          valueName = children;
        }
      }

      // Final fallback
      if (!valueName && filterItem.value) {
        valueName = String(filterItem.value);
      }

      return valueName ? `${fieldName} ${operator} ${valueName}` : `${fieldName} ${operator}`;
    }

    return `${fieldName} ${operator}`;
  }, []);

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    setFilters([{ filterBy: '', isOptions: '_in', filterValue: '' }]); // Default to "is"
    setFinalFilters([{ filterBy: '', isOptions: '_in', filterValue: '' }]); // Default to "is"
    refetch();
  }, [refetch]);

  // Calculate active filter count
  const activeFilterCount = useMemo(
    () => finalFilters.filter(f => f.filterBy && f.filterValue).length,
    [finalFilters]
  );

  // Handle filter chip click to toggle inline dropdown
  const handleFilterChipClick = useCallback((index, e) => {
    e.stopPropagation();

    if (editingFilterIndex === index) {
      // Closing the dropdown
      chipDropdownRef.current = null;
      setEditingFilterIndex(null);
    } else {
      // Opening the dropdown - store reference to the clicked chip element
      chipDropdownRef.current = e.currentTarget;

      // Calculate position for Portal dropdown
      const chipRect = e.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: chipRect.bottom + 4,
        left: chipRect.left
      });
      setEditingFilterIndex(index);
    }
  }, [editingFilterIndex]);

  // Outside click is now handled by FilterChipDropdown component

  const handleTogglePopover = () => {
    setShowPopover((show) => !show);
  };

  const NewBriefPopover = (
    <Popover>
      <BSButton
        classList="button secondary_btn"
        ButtonText="Boost Plan"
        variant="light"
        onClick={() => navigate(ROUTES.Project.NewPlan(projectId))}
      />
      <BSButton
        classList="button secondary_btn"
        ButtonText="New Brief"
        variant="outline-secondary"
        onClick={() => {
          handleTogglePopover();
          onCreateHandler();
        }}
      />
    </Popover>
  );

  // const [showNestedDropdown, setShowNestedDropdown] = useState(false);

  const handleItemClick = useCallback((item) => {
    // if (item.modalKey === 'expandPlan') {
    //   setShowNestedDropdown(!showNestedDropdown);
    // }
    setModalView({
      expandPlan: false,
      createBrief: false,
      [item.modalKey]: true,
    });
  }, []);

  const handleAICreate = useCallback(async (taskId, projectId, taskName) => {
    // Update ref IMMEDIATELY (synchronously) before any async operations
    // This ensures handleContentGenerated always has the correct values
    taskInfoRef.current = { taskId, taskName, projectId };
    setTaskInfo({ taskId, taskName, projectId });

    abortController.current = new AbortController();
    setStreamContent('');
    setContentGenerated(false);
    setShowContentGeneration(true);

    try {
      const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data_style_guides`;
      const BODY = JSON.stringify({
        project_id: projectId,
        task_id: taskId,
      });

      await generateStreamResponse(
        URL,
        BODY,
        setStreamContent,
        setContentGenerated,
        abortController?.current.signal,
      );
    } catch (error) {
      errorToast(error);
    }
  }, []);

  const handleContentGenerated = async () => {
    // Use refs to avoid stale closure issues - refs always have the latest values
    const taskName = taskInfoRef.current?.taskName;
    const taskId = taskInfoRef.current?.taskId;
    const projectId = taskInfoRef.current?.projectId;
    await createDocument(
      {
        document: {
          title: taskName,
          user_id: userId,
          content: streamContentRef.current,
        },
        taskId,
        projectId,
      },
      {
        onSuccess: (documentResponse) => {
          const documentId = documentResponse?.data?.document?.id;
          if (!documentId) return;

          // Update only the specific task in cache - preserves current sort order
          const queryKey = keys.Briefs({
            searchValue,
            filterStr: parseFilters(finalFilters),
            selectedProject: projectId || selectedProject,
            type: '',
          });

          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: {
                  ...page.data,
                  tasks: page.data.tasks.map((task) =>
                    task.id === taskId
                      ? {
                          ...task,
                          document_id: documentId,
                          state: 'in_progress',
                          document: documentResponse?.data?.document,
                        }
                      : task
                  ),
                },
              })),
            };
          });

          navigate(ROUTES.FreestyleDocumentTemplate(documentId));
        },
      },
    );
  };

  // Bulk delete handlers
  const handleTaskSelect = useCallback((taskId, isSelected) => {
    if (isSelected) {
      setSelectedTaskIds((prev) => [...(prev || []), taskId]);
    } else {
      setSelectedTaskIds((prev) => (prev || []).filter((id) => id !== taskId));
    }
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!tasks || tasks.length === 0) return;

    if ((selectedTaskIds || []).length === tasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(tasks.map((task) => task.id));
    }
  }, [tasks, selectedTaskIds]);

  const handleSelectAllInColumn = useCallback((category) => {
    if (!tasks || tasks.length === 0) return;

    // Filter tasks by category
    const columnTasks = tasks.filter((task) => task.state === category);
    const columnTaskIds = columnTasks.map((task) => task.id);

    // Check if all tasks in this column are already selected
    const allColumnSelected = columnTaskIds.every((id) =>
      selectedTaskIds.includes(id),
    );

    if (allColumnSelected) {
      // Deselect all tasks in this column
      setSelectedTaskIds((prev) =>
        prev.filter((id) => !columnTaskIds.includes(id)),
      );
    } else {
      // Select all tasks in this column (add to existing selection)
      setSelectedTaskIds((prev) => {
        const newSelection = [...prev];
        columnTaskIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  }, [tasks, selectedTaskIds]);

  // Helper functions for Kanban column selection state
  const getColumnSelectionState = useCallback((category) => {
    if (!tasks || tasks.length === 0)
      return { isAllSelected: false, isIndeterminate: false };

    const columnTasks = tasks.filter((task) => task.state === category);
    const columnTaskIds = columnTasks.map((task) => task.id);
    const selectedColumnTasks = columnTaskIds.filter((id) =>
      selectedTaskIds.includes(id),
    );

    const isAllSelected =
      columnTaskIds.length > 0 &&
      selectedColumnTasks.length === columnTaskIds.length;
    const isIndeterminate =
      selectedColumnTasks.length > 0 &&
      selectedColumnTasks.length < columnTaskIds.length;

    return { isAllSelected, isIndeterminate };
  }, [tasks, selectedTaskIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedTaskIds([]);
  }, []);

  const handleBulkDelete = useCallback(() => {
    setShowBulkDeleteModal(true);
  }, []);

  const handleConfirmBulkDelete = useCallback(async () => {
    if (isBulkDeleting) {
      return;
    }

    try {
      await bulkDeleteTasks(selectedTaskIds);
      setSelectedTaskIds([]);
      setShowBulkDeleteModal(false);

      // Force refetch the data to update the UI
      setTimeout(() => {
        refetch();
      }, 100);
    } catch (error) {
      // Error is handled by the hook
    }
  }, [isBulkDeleting, bulkDeleteTasks, selectedTaskIds, refetch]);

  return (
    <section className={Styles.ContentPlanner}>
      {/* {projectView && <Announcement view={true}/>} */}

      <section
        className={Styles.ContentPlanner__Wrapper}
        style={{ marginTop: noMargin === true ? 0 : !projectView ? '20px' : 0 }}
      >
        <Tab.Container
          className="nav-tabs"
          id="plan-tabs"
          activeKey={activeTab}
          onSelect={(key) => {
            setActiveTab(key);
            localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, key);
          }}
        >
          <Nav className={Styles.ContentPlanner__Wrapper_Navigation}>
            <div className={Styles.ContentPlanner__Wrapper_Navigation__left}>
              <Nav.Item
                className={Styles.ContentPlanner__Wrapper_Navigation_Icon}
              >
                <Nav.Link
                  eventKey="kanban"
                  className={
                    Styles.ContentPlanner__Wrapper_Navigation_Icon__Item
                  }
                >
                  <Kanban size={20} />
                  <span>Kanban view</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item
                className={Styles.ContentPlanner__Wrapper_Navigation_Icon}
              >
                <Nav.Link
                  eventKey="list"
                  className={
                    Styles.ContentPlanner__Wrapper_Navigation_Icon__Item
                  }
                >
                  <List size={20} />
                  <span>List view</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item
                className={Styles.ContentPlanner__Wrapper_Navigation_Icon}
              >
                <Nav.Link
                  eventKey="calendar"
                  className={
                    Styles.ContentPlanner__Wrapper_Navigation_Icon__Item
                  }
                >
                  <Calendar size={20} />
                  <span>Calendar view</span>
                </Nav.Link>
              </Nav.Item>
            </div>
            <div className={Styles.ContentPlanner__Wrapper_Navigation__right}>
              {activeTab === 'calendar' && tasks?.length >= 0 && (
                <div
                  className={
                    Styles.ContentPlanner__Wrapper_Navigation__Calendar_Large
                  }
                >
                  <button
                    onClick={handlePreviousMonth}
                    className={Styles.calendar_btn}
                  >
                    <FaChevronLeft />
                  </button>
                  <span className={Styles.calendar_month_text}>
                    {formattedMonthYear}
                  </span>
                  <button
                    onClick={handleNextMonth}
                    className={Styles.calendar_btn}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
              <div className="px-2">
                <SearchInput
                  setInputValue={setSearchValue}
                  inputValue={searchValue}
                  text="Search"
                />
              </div>
              <Dropdown align="end">
                <Dropdown.Toggle
                  className={Styles.ContentPlanner__Wrapper_Navigation_Filter}
                >
                  <BsFilter size={20} />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className={Styles.FilterBadge}>
                      {activeFilterCount}
                    </span>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  className={
                    Styles.ContentPlanner__Wrapper_InputSection_DropDown
                  }
                  style={{
                    overflow: 'visible',
                    maxHeight: 'none',
                    zIndex: 1050,
                  }}
                  popperConfig={{
                    strategy: 'fixed',
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          boundary: 'viewport',
                        },
                      },
                      {
                        name: 'flip',
                        enabled: true,
                        options: {
                          fallbackPlacements: ['bottom-end', 'top-end'],
                        },
                      },
                      {
                        name: 'offset',
                        enabled: true,
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  }}
                  renderOnMount={true}
                >
                  {filters.map((filter, index) => {
                    const { filterBy, isOptions, filterValue } = filter || {};

                    const filterByValue = options.find(
                      (status) => status.value === filterBy,
                    );

                    return (
                      <div
                        className={
                          'justify-content-between ' +
                          Styles.ContentPlanner__Wrapper_InputSection_Filters
                        }
                        style={{ marginBottom: 4, gap: '6px' }}
                        key={index}
                      >
                        <section className="d-flex justify-content-start" style={{ gap: '6px' }}>
                          <Select
                            isSearchable={false}
                            className={'filter-Select'}
                            classNamePrefix={'filter-Select'}
                            styles={SelectStyles}
                            placeholder="Filter By"
                            options={options.filter(
                              (option) =>
                                !appliedFilters.includes(option.value),
                            )}
                            value={filterByValue || null}
                            onChange={(val) =>
                              handleFilters(index, val.value, 'filterBy')
                            }
                          />
                          {/* is/is not dropdown removed - now defaults to "is" */}
                          {/* Users can toggle on the chip itself if needed */}
                          <ContentFilter
                            filterBy={filterBy}
                            filterValue={filterValue}
                            handleFilters={handleFilters}
                            index={index}
                            filters={filters}
                            isOptions={isOptions}
                            project={projectId || selectedProject}
                          />
                        </section>

                        <RxCross2
                          size={14}
                          color="#667085"
                          onClick={() => handleRemoveFilter(index)}
                        />
                      </div>
                    );
                  })}

                  {filters.length < 4 && (
                    <BSButton
                      icon={<BsPlus />}
                      classList={`button button-light ${Styles.addFilterBtn}`}
                      ButtonText="Add filter"
                      variant="light"
                      onClick={() =>
                        setFilters((prevValue) => [
                          ...prevValue,
                          { filterBy: '', isOptions: '_in' }, // Default to "is"
                        ])
                      }
                    />
                  )}
                </Dropdown.Menu>
              </Dropdown>
              {activeTab === 'calendar' && (
                <div className={Styles.utility_left_margin}>
                  <ViewToggle
                    activeView={calendarView}
                    onViewChange={(view) => {
                      setCalendarView(view);
                      localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, view);
                    }}
                  />
                </div>
              )}
              {activeTab === 'list' && (
                <div
                  className={`dropdown_container ${Styles.utility_left_margin}`}
                >
                  <ProjectSelect
                    setSelectedProject={setSelectedProject}
                    onViewModeChange={handleViewModeChange}
                    currentViewMode={listViewMode}
                  />
                </div>
              )}
            </div>
            {activeTab === 'calendar' && tasks?.length >= 0 && (
              <div
                className={Styles.ContentPlanner__Wrapper_Navigation__Calendar}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={handlePreviousMonth}
                      className={Styles.calendar_btn}
                    >
                      <FaChevronLeft />
                    </button>
                    <span className={Styles.calendar_month_text}>
                      {formattedMonthYear}
                    </span>
                    <button
                      onClick={handleNextMonth}
                      className={Styles.calendar_btn}
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                  <ViewToggle
                    activeView={calendarView}
                    onViewChange={(view) => {
                      setCalendarView(view);
                      localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, view);
                    }}
                  />
                </div>
              </div>
            )}
          </Nav>

          {/* Active Filter Chips */}
          {activeFilterCount > 0 && (
            <div className={Styles.ActiveFilters}>
              <div className={Styles.ActiveFilters__Container}>
                {finalFilters.map((filter, index) => {
                  if (!filter.filterBy || !filter.filterValue) return null;

                  const label = getFilterLabel(filter);
                  const operator = filter.isOptions === '_in' ? 'is' : 'is not';

                  // Split label into parts for visual hierarchy
                  // Format: "Field operator value" -> ["Field", "operator", "value"]
                  const parts = label.match(/^(.+?)\s+(is|is not)\s+(.+)$/);
                  const fieldName = parts ? parts[1] : label;
                  const operatorText = parts ? parts[2] : '';
                  const valueName = parts ? parts[3] : '';

                  const filterByValue = options.find(
                    (option) => option.value === filter.filterBy
                  );

                  return (
                    <div
                      key={index}
                      className={Styles.FilterChip__Wrapper}
                    >
                      <div
                        className={`${Styles.FilterChip} ${Styles.FilterChip__Clickable} ${
                          editingFilterIndex === index ? Styles.FilterChip__Active : ''
                        }`}
                        onClick={(e) => handleFilterChipClick(index, e)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleFilterChipClick(index, e);
                          }
                        }}
                        title="Click to edit filter"
                      >
                        <span className={Styles.FilterChip__Label}>
                          <span className={Styles.FilterChip__Field}>{fieldName}</span>
                          {operatorText && (
                            <>
                              <span className={Styles.FilterChip__Operator}>{operatorText}</span>
                              <span className={Styles.FilterChip__Value}>{valueName}</span>
                            </>
                          )}
                        </span>

                        {/* Dropdown arrow */}
                        <FaChevronDown
                          size={10}
                          className={`${Styles.FilterChip__Arrow} ${
                            editingFilterIndex === index ? Styles.FilterChip__Arrow__Open : ''
                          }`}
                        />

                        {/* Remove filter button */}
                        <button
                          className={Styles.FilterChip__Remove}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter(index);
                          }}
                          aria-label={`Remove filter: ${label}`}
                          type="button"
                        >
                          <RxCross2 size={14} />
                        </button>
                      </div>

                    </div>
                  );
                })}

                {activeFilterCount > 1 && (
                  <button
                    className={Styles.ClearAllBtn}
                    onClick={handleClearAllFilters}
                    type="button"
                    aria-label={`Clear all ${activeFilterCount} filters`}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Render chip dropdown via Portal - completely separate from layout */}
          {editingFilterIndex !== null && (
            <FilterChipDropdown
              filterBy={finalFilters[editingFilterIndex]?.filterBy}
              filterValue={finalFilters[editingFilterIndex]?.filterValue}
              position={dropdownPosition}
              project={projectId || selectedProject}
              triggerRef={chipDropdownRef}
              onSelect={(option) => {
                const { value, label } = option;
                const currentFilter = finalFilters[editingFilterIndex];

                if (
                  Array.isArray(currentFilter.filterValue) &&
                  currentFilter.filterValue.some((filterVal) => filterVal.value === value)
                ) {
                  // Remove
                  const updated = currentFilter.filterValue.filter((val) => val.value !== value);
                  handleFilters(editingFilterIndex, updated, 'filterValue');
                } else {
                  // Add
                  handleFilters(
                    editingFilterIndex,
                    [...(currentFilter.filterValue || []), { value, label }],
                    'filterValue',
                  );
                }
              }}
              onClose={() => {
                chipDropdownRef.current = null;
                setEditingFilterIndex(null);
              }}
            />
          )}

          <Tab.Content>
            {isLoading && !briefsPages ? (
              <div className={Styles.ContentPlanner__Loading}>
                <Loader inline loaderHeading="Loading Briefs..." />
              </div>
            ) : (!tasks || tasks.length === 0) && !isFetching ? (
              <EmptyState
                emptyText="Let's create your first content plan"
                ctaText={
                  projectView
                    ? 'with posts tailored to your brand.'
                    : 'Generate a content plan.'
                }
                buttonText={projectView ? 'Create new plan' : 'Boost Plan'}
                onClick={
                  projectView
                    ? () => setModalView((prevState) => ({ ...prevState, expandPlan: true }))
                    : currentSubscription?.plan_name === 'Basic Plan'
                      ? () => setShowUpgradeModal(true)
                      : () => navigate(ROUTES.Project.NewPlan(projectId))
                }
              >
                <ImageView src="/Images/plan.svg" />
              </EmptyState>
            ) : (
              <>
                <Tab.Pane eventKey="kanban">
                  <BulkActionToolbar
                    selectedCount={selectedTaskIds?.length || 0}
                    onBulkDelete={handleBulkDelete}
                    isLoading={isBulkDeleting}
                    onClearSelection={handleClearSelection}
                    itemType="briefs"
                  />
                  <KanbanBoard
                    onCreateClick={onCreateHandler}
                    tasks={tasks || []}
                    onDelete={onDeleteHandler}
                    onMove={onMoveHandler}
                    onEdit={onEditHandler}
                    fetchCPTasks={refetch}
                    onArchive={onArchive}
                    next={fetchNextPage}
                    hasMore={hasNextPage}
                    setTasks={setTasks}
                    handleAICreate={handleAICreate}
                    showCheckbox={true}
                    selectedTaskIds={selectedTaskIds || []}
                    onTaskSelect={handleTaskSelect}
                    onSelectAll={handleSelectAllInColumn}
                    getColumnSelectionState={getColumnSelectionState}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="list">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <BulkActionToolbar
                      selectedCount={selectedTaskIds?.length || 0}
                      onBulkDelete={handleBulkDelete}
                      isLoading={isBulkDeleting}
                      onClearSelection={handleClearSelection}
                      itemType="briefs"
                    />
                  </div>
                  <KanbanList
                    tasks={tasks || []}
                    onDelete={onDeleteHandler}
                    onMove={onMoveHandler}
                    onEdit={onEditHandler}
                    fetchCpTasks={refetch}
                    onArchive={onArchive}
                    onUpdateBrief={onUpdateBrief}
                    selectedColumns={selectedColumns}
                    updateColumns={updateColumns}
                    handleAICreate={handleAICreate}
                    showCheckbox={true}
                    selectedTaskIds={selectedTaskIds || []}
                    onTaskSelect={handleTaskSelect}
                    onSelectAll={handleSelectAll}
                    isAllSelected={
                      selectedTaskIds.length === (tasks?.length || 0) &&
                      (tasks?.length || 0) > 0
                    }
                    isIndeterminate={
                      selectedTaskIds.length > 0 &&
                      selectedTaskIds.length < (tasks?.length || 0)
                    }
                    viewMode={listViewMode}
                    projects={projects}
                    brands={brands}
                  />
                  {/* <table className="w-100">
                    <TableSkeleton />
                  </table> */}
                </Tab.Pane>
                <Tab.Pane eventKey="calendar">
                  <Calender
                    tasks={tasks || []}
                    onMove={onMoveHandler}
                    onEdit={onEditHandler}
                    onDuplicate={onDuplicateHandler}
                    onDelete={onDeleteHandler}
                    fetchCpTasks={refetch}
                    onArchive={onArchive}
                    calendarProps={{ ref: calendarRef, headerToolbar: false }}
                    onUpdateBrief={onUpdateBrief}
                    handleAICreate={handleAICreate}
                    calendarView={calendarView}
                    onCreatePost={onCreatePostWithDate}
                    onQuickUpdateDeadline={onQuickUpdateDeadline}
                    currentDate={date}
                  />
                </Tab.Pane>
              </>
            )}
          </Tab.Content>
        </Tab.Container>
      </section>

      {/* {projectId ? ( */}
      {/* <OverlayTrigger
          placement="top"
          rootClose
          show={showPopover}
          onToggle={handleTogglePopover}
          overlay={NewBriefPopover}
        > */}
      {/* <FloatingButton onClick={handleTogglePopover} /> */}
      {/* <DropdownButton
        drop={'down-centered'}
        title={<LuPlus className={Styles.plus_icon} />}
        className={`${Styles.rounded_plus_btn}`}
        variant="dark"
      >
        {dropdownItems.map((item) => (
          <React.Fragment key={item.modalKey}>
            <Dropdown.Item
              onClick={() => handleItemClick(item)}
              className={Styles.list_item}
            >
              <item.Icon color="#00A7B7" size={14} />
              <span className="text-md-Regular">{item.label}</span>
            </Dropdown.Item>
            {item.modalKey === 'expandPlan' && showNestedDropdown && (
              <DropdownButton
                drop={'right'}
                title="More Options"
                className={Styles.nested_dropdown}
                variant="light"
                show={showNestedDropdown}
                onToggle={() => setShowNestedDropdown(!showNestedDropdown)}
              >
                <Dropdown.Item
                  onClick={() => console.log('Expanded Option 1 Clicked')}
                >
                  Expanded Option 1
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => console.log('Expanded Option 2 Clicked')}
                >
                  Expanded Option 2
                </Dropdown.Item>
              </DropdownButton>
            )}
          </React.Fragment>
        ))}
      </DropdownButton> */}
      <MultiDropdown
        onClick={handleItemClick}
        higherIndex={true}
        projectView={projectView}
      />
      {/* </OverlayTrigger> */}
      {/* // ) 
      // : (
      //   <FloatingButton onClick={() => onCreateHandler()} />
      // )
      } */}

      {deleteTaskModalView && (
        <ConfirmDeleteModal
          show={deleteTaskModalView}
          onHide={() => {
            setDeleteTaskModalView(false);
            setTaskIdToDelete(null);
          }}
          onDelete={onConfirmDeleteHandler}
        />
      )}

      {modalView.createBrief && (
        <BriefDrawer
          action="create"
          show={modalView.createBrief}
          onHide={() => {
            setModalView((prevState) => ({
              ...prevState,
              createBrief: false,
            }));
          }}
          onSubmit={onSubmitHandler}
          task={newTask}
          isProjectView={!!projectId}
        />
      )}

      {modalView.expandPlan && (
        <ExpandPlanDrawer
          show={modalView.expandPlan}
          onHide={() => {
            setModalView((prevState) => ({
              ...prevState,
              expandPlan: false,
            }));
          }}
          callback={refetch}
          title={'Expand Plan'}
          placement="end"
          width={720}
          isProjectView={!!projectId}
          projectId={projectId}
          campaignBrandId={campaignBrandId}
          campaignStartDate={campaignData?.start_date}
          campaignEndDate={campaignData?.end_date}
        />
      )}

      {editTaskModalView && (
        <BriefDrawer
          action="edit"
          show={editTaskModalView}
          onHide={() => {
            setEditTaskModalView(false);
            setTaskIdToEdit(null);
            briefId && navigate(ROUTES.Planner);
          }}
          onSubmit={onSubmitHandler}
          onDeleteHandler={onDeleteHandler}
          task={tasks?.find((task) => {
            return String(task.id) === String(taskIdToEdit);
          })}
          isProjectView={!!projectId}
        />
      )}

      {moveToStateModalView && (
        <MoveToStateModal
          show={moveToStateModalView}
          onHide={() => {
            setMoveToStateModalView(false);
            setTaskIdToEdit(null);
          }}
          onMove={onConfirmEditHandler}
          task={tasks?.find((task) => {
            return String(task.id) === String(taskIdToEdit);
          })}
        />
      )}

      <ContentGenerationModal
        onHide={() => {
          setShowContentGeneration(false);
          setStreamContent('');
          setContentGenerated(false);
          abortController?.current.abort();
        }}
        show={showContentGeneration}
        string={streamContent}
        contentGenerated={contentGenerated}
      />

      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
        />
      )}

      <BulkDeleteConfirmModal
        show={showBulkDeleteModal}
        onHide={() => {
          if (!isBulkDeleting) {
            setShowBulkDeleteModal(false);
          }
        }}
        onConfirm={handleConfirmBulkDelete}
        selectedCount={selectedTaskIds?.length || 0}
        isLoading={isBulkDeleting}
        itemType="briefs"
      />
    </section>
  );
};

export default ContentPlanner;
